/**
 * Error reporting, ADR 0038: our own first, a vendor later if it earns it.
 *
 * Everything that reports an error calls `reportError`. There is exactly one destination, set once per app with
 * `configureErrorReporting`; a vendor later is a second sink behind the same function, and no feature code ever
 * names one. Nothing personal is sent: every text field is scrubbed here, before it leaves the page, and again in the
 * database before it is stored (`private.scrub_error_text`). The two use the same rules, kept in step by their tests.
 */

/** Where an error happened. The same names the audit log uses for the surfaces that can fail in front of a person. */
export type ErrorSurface =
  "control_plane" | "staff_dashboard" | "member_portal" | "public_web" | "edge_function";

/** What is sent, and all that is sent (ADR 0038, decision 2). No form values, bodies, names or addresses. */
export interface ErrorReport {
  /** The short reference the person sees and can read to support, e.g. E-7K3Q-9MZD. */
  readonly reference: string;
  readonly surface: ErrorSurface;
  /** The route pattern (/people/:id), never the filled path, the query or the fragment. */
  readonly route: string;
  readonly errorClass: string;
  readonly message: string;
  readonly stack: string | null;
  readonly release: string;
  readonly correlationId: string | null;
  readonly occurredAt: string;
}

/** A destination. Today there is one: the report-error Edge Function, which stores the report in our own table. */
export type ErrorSink = (report: ErrorReport) => Promise<void>;

const MAX_MESSAGE = 2000;
const MAX_STACK = 16000;
const MAX_ROUTE = 300;

/*
 * The scrubbing rules, in the order they run. Each pattern is written in the subset JavaScript and PostgreSQL's
 * regular expressions share, and private.scrub_error_text applies the same list in the same order.
 */
const SCRUB_RULES: readonly (readonly [RegExp, string])[] = [
  // A query string or fragment carries search terms and form values; the path before it is kept.
  [/(https?:\/\/[^\s?#"']+)[?#][^\s"']*/g, "$1"],
  // The same for a bare path, such as a route in a message: /people?q=Ada loses its query.
  [/(^|[\s"'(])(\/[^\s?#"')]*)[?#][^\s"')]*/g, "$1$2"],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]"],
  [/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, "[jwt]"],
  [/sb_(publishable|secret)_[A-Za-z0-9_-]+/g, "[key]"],
  [/Bearer\s+[A-Za-z0-9._~+/-]+=*/gi, "[redacted]"],
  // A value under a credential-shaped key, quoted or not: password=..., "api_key": "...", token: ...
  [
    /((?:token|secret|password|passwd|authorization|cookie|api[_-]?key|apikey|credential)["']?\s*[:=]\s*)("[^"]*"|'[^']*'|[^\s,;&"'}]+)/gi,
    "$1[redacted]",
  ],
  // Seven to fifteen digits with the usual separators. The edges refuse letters and a hyphen before hex, so the
  // groups of a UUID, which support needs, are never mistaken for a telephone number.
  [/(?<![0-9A-Za-z-])\+?\d(?:[ ().-]{0,2}\d){6,14}(?![0-9A-Za-z])(?!-[0-9A-Fa-f])/g, "[phone]"],
];

/** Removes email addresses, telephone numbers, credentials and query strings from a piece of text. */
export function scrubText(text: string): string {
  let out = text;
  for (const [pattern, replacement] of SCRUB_RULES) out = out.replace(pattern, replacement);
  return out;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The route pattern of a path or URL: no origin, query or fragment, and ids and numbers replaced by placeholders. */
export function routePattern(pathOrUrl: string): string {
  let path = pathOrUrl.replace(/^[a-z]+:\/\/[^/]+/i, "");
  path = path.split(/[?#]/)[0] ?? "";
  const pattern = path
    .split("/")
    .map((segment) => (UUID.test(segment) ? ":id" : /^\d+$/.test(segment) ? ":n" : segment))
    .join("/");
  const normalized = pattern.startsWith("/") ? pattern : `/${pattern}`;
  return normalized.slice(0, MAX_ROUTE);
}

// Crockford's base 32: no I, L, O or U, so a reference read aloud over the telephone cannot be misheard.
const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/** A short reference a person can read to support, E-XXXX-XXXX. About a trillion values, so collisions are rare. */
export function newErrorReference(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  const chars = Array.from(bytes, (b) => ALPHABET[b % 32] ?? "0").join("");
  return `E-${chars.slice(0, 4)}-${chars.slice(4, 8)}`;
}

interface ReportingConfig {
  readonly surface: ErrorSurface;
  readonly release: string;
  readonly sink?: ErrorSink | undefined;
}

let config: ReportingConfig = { surface: "staff_dashboard", release: "unknown" };

/** Sets the one destination for this app. Called once, where each app wires its providers. */
export function configureErrorReporting(next: ReportingConfig): void {
  config = next;
}

export interface ReportContext {
  /** A path or URL; only its pattern is kept. */
  readonly route?: string;
  readonly correlationId?: string;
}

function describeThrown(error: unknown): {
  errorClass: string;
  message: string;
  stack: string | null;
} {
  if (error instanceof Error) {
    return { errorClass: error.name, message: error.message, stack: error.stack ?? null };
  }
  // Error-shaped objects that are not Errors, such as the database client's errors: { message, code, name? }.
  if (
    typeof error === "object" &&
    error !== null &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    const shaped = error as { message: string; name?: unknown; code?: unknown };
    const name = typeof shaped.name === "string" && shaped.name !== "" ? shaped.name : "ErrorLike";
    const code = typeof shaped.code === "string" && shaped.code !== "" ? ` ${shaped.code}` : "";
    return { errorClass: `${name}${code}`, message: shaped.message, stack: null };
  }
  return {
    errorClass: typeof error,
    message: typeof error === "string" ? error : "a value that is not an Error was thrown",
    stack: null,
  };
}

/** Builds the report that would be sent, already scrubbed. Exported for the Edge Functions, which send it themselves. */
export function buildErrorReport(error: unknown, context: ReportContext = {}): ErrorReport {
  const thrown = describeThrown(error);
  return {
    reference: newErrorReference(),
    surface: config.surface,
    route: routePattern(context.route ?? ""),
    errorClass: scrubText(thrown.errorClass).slice(0, 120),
    message: scrubText(thrown.message).slice(0, MAX_MESSAGE),
    stack: thrown.stack === null ? null : scrubText(thrown.stack).slice(0, MAX_STACK),
    release: config.release.slice(0, 80),
    correlationId: context.correlationId ?? null,
    occurredAt: new Date().toISOString(),
  };
}

/*
 * One failure, one report. React renders a failing component again to try to recover, and the component throws a new
 * error object each time; a script error can also reach more than one listener. The same failure seen again within a
 * few seconds gets the reference it already has and is not sent twice.
 */
const DUPLICATE_WINDOW_MS = 5000;
const recent = new Map<string, { reference: string; at: number }>();

/**
 * Reports an error and returns the reference to show the person. It never throws and never waits: a failure to report
 * must not become a second failure in front of someone, and the reference is shown whether or not it was delivered.
 */
export function reportError(error: unknown, context: ReportContext = {}): string {
  let report: ErrorReport;
  try {
    report = buildErrorReport(error, context);
  } catch {
    return newErrorReference();
  }
  const key = [report.surface, report.route, report.errorClass, report.message].join("\u0000");
  const now = Date.now();
  const seen = recent.get(key);
  if (seen !== undefined && now - seen.at < DUPLICATE_WINDOW_MS) return seen.reference;
  recent.set(key, { reference: report.reference, at: now });
  if (recent.size > 100) recent.clear();
  const sink = config.sink;
  if (sink !== undefined) {
    try {
      sink(report).catch(() => undefined);
    } catch {
      // A sink that throws synchronously is as unreachable as one that rejects.
    }
  }
  return report.reference;
}

/**
 * The sink every surface uses today: the report-error Edge Function, which works out the church from the address the
 * report came from and the person from their session, and never from anything in the report itself.
 */
export function createHttpSink(
  endpoint: string,
  getAccessToken: () => Promise<string | null> = () => Promise.resolve(null),
): ErrorSink {
  return async (report) => {
    const token = await getAccessToken().catch(() => null);
    await fetch(endpoint, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(report),
    });
  };
}
